import { useCallback, useEffect, useRef, useState } from "react";

import type { ParticipationApiId } from "../../shared/participation";

const REFRESH_PATH = "/api/participation-refresh";
const REFRESH_ERROR_MESSAGE =
  "参加状況の元データを更新できません。設定を確認して手動で再試行してください。";
const REFRESH_WINDOW_CLOSED_MESSAGE =
  "予約データの更新が許可期間外です。サーバーの更新許可期間とイベント開始日時を確認してください。";
export const PARTICIPATION_REFRESH_REQUEST_TIMEOUT_MS = 25_000;

type RefreshStatus = "updated" | "cooldown" | "in-progress";

interface RefreshSuccess {
  readonly ok: true;
  readonly data: {
    readonly status: RefreshStatus;
  };
}

interface RefreshWindowClosedError {
  readonly ok: false;
  readonly error: {
    readonly code: "REFRESH_WINDOW_CLOSED";
    readonly message: string;
    readonly requestId: string;
  };
}

export interface ParticipationRefreshController {
  readonly refreshing: boolean;
  readonly windowClosed: boolean;
  readonly error: string | null;
  readonly refresh: (
    apiId: ParticipationApiId,
    targetEpochMs: number,
  ) => Promise<boolean>;
}

function isRefreshSuccess(value: unknown): value is RefreshSuccess {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).length !== 2
    || record.ok !== true
    || typeof record.data !== "object"
    || record.data === null
    || Array.isArray(record.data)
  ) {
    return false;
  }
  const data = record.data as Record<string, unknown>;
  if (Object.keys(data).length !== 1) return false;
  const status = data.status;
  return status === "updated" || status === "cooldown" || status === "in-progress";
}

function isRefreshWindowClosedError(value: unknown): value is RefreshWindowClosedError {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  if (
    Object.keys(record).length !== 2
    || record.ok !== false
    || typeof record.error !== "object"
    || record.error === null
    || Array.isArray(record.error)
  ) {
    return false;
  }
  const error = record.error as Record<string, unknown>;
  return (
    Object.keys(error).length === 3
    && error.code === "REFRESH_WINDOW_CLOSED"
    && typeof error.message === "string"
    && typeof error.requestId === "string"
  );
}

export function useParticipationRefresh(): ParticipationRefreshController {
  const mountedRef = useRef(true);
  const pendingByApiRef = useRef<Partial<Record<ParticipationApiId, Promise<boolean>>>>({});
  const pendingCountRef = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [windowClosed, setWindowClosed] = useState(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback((
    apiId: ParticipationApiId,
    targetEpochMs: number,
  ): Promise<boolean> => {
    const pending = pendingByApiRef.current[apiId];
    if (pending) return pending;

    const operation = (async (): Promise<boolean> => {
      pendingCountRef.current += 1;
      if (mountedRef.current) {
        setRefreshing(true);
        setError(null);
      }

      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => {
        controller.abort("participation refresh request timeout");
      }, PARTICIPATION_REFRESH_REQUEST_TIMEOUT_MS);

      try {
        const response = await fetch(REFRESH_PATH, {
          method: "POST",
          cache: "no-store",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-Zenyasai-Projection": "1",
          },
          body: JSON.stringify({ apiId, targetEpochMs }),
          signal: controller.signal,
        });
        const payload: unknown = await response.json();
        if (!response.ok || !isRefreshSuccess(payload)) {
          if (mountedRef.current) {
            const closed = response.status === 403 && isRefreshWindowClosedError(payload);
            setWindowClosed(closed);
            setError(
              closed
                ? REFRESH_WINDOW_CLOSED_MESSAGE
                : REFRESH_ERROR_MESSAGE,
            );
          }
          return false;
        }
        if (mountedRef.current) setWindowClosed(false);
        return true;
      } catch {
        if (mountedRef.current) {
          setWindowClosed(false);
          setError(REFRESH_ERROR_MESSAGE);
        }
        return false;
      } finally {
        window.clearTimeout(timeoutId);
        pendingCountRef.current -= 1;
        if (mountedRef.current) setRefreshing(pendingCountRef.current > 0);
      }
    })();

    pendingByApiRef.current[apiId] = operation;
    void operation.finally(() => {
      if (pendingByApiRef.current[apiId] === operation) {
        delete pendingByApiRef.current[apiId];
      }
    });
    return operation;
  }, []);

  return { refreshing, error, windowClosed, refresh };
}
